/**
 * Accessibility Utilities
 *
 * Helper functions for improving accessibility across the app.
 */

/**
 * Generate a unique ID for accessibility purposes
 */
export function generateA11yId(prefix: string = 'a11y'): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Trap focus within an element (for modals/dialogs)
 */
export function trapFocus(container: HTMLElement): () => void {
  const focusableSelectors = [
    'button:not([disabled])',
    'a[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable]',
  ];

  const focusableElements = container.querySelectorAll<HTMLElement>(
    focusableSelectors.join(', ')
  );

  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  // Store the element that was focused before opening
  const previouslyFocused = document.activeElement as HTMLElement;

  // Focus the first focusable element
  if (firstFocusable) {
    firstFocusable.focus();
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      // Shift + Tab: if on first element, go to last
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable?.focus();
      }
    } else {
      // Tab: if on last element, go to first
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable?.focus();
      }
    }
  };

  container.addEventListener('keydown', handleKeyDown);

  // Return cleanup function
  return () => {
    container.removeEventListener('keydown', handleKeyDown);
    // Restore focus to the previously focused element
    previouslyFocused?.focus();
  };
}

/**
 * Announce a message to screen readers
 */
export function announceToScreenReader(
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void {
  // Find or create the live region
  let liveRegion = document.getElementById('sr-live-region');

  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.id = 'sr-live-region';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only'; // visually hidden
    liveRegion.style.cssText = `
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    `;
    document.body.appendChild(liveRegion);
  }

  // Update the live region
  liveRegion.setAttribute('aria-live', priority);
  liveRegion.textContent = message;

  // Clear after a delay to allow for repeated announcements
  setTimeout(() => {
    if (liveRegion) {
      liveRegion.textContent = '';
    }
  }, 1000);
}

/**
 * Get ARIA props for a button that opens a menu
 */
export function getMenuButtonProps(
  isOpen: boolean,
  menuId: string
): Record<string, unknown> {
  return {
    'aria-expanded': isOpen,
    'aria-haspopup': 'menu',
    'aria-controls': isOpen ? menuId : undefined,
  };
}

/**
 * Get ARIA props for a disclosure (expandable section)
 */
export function getDisclosureProps(
  isOpen: boolean,
  contentId: string
): { button: Record<string, unknown>; content: Record<string, unknown> } {
  return {
    button: {
      'aria-expanded': isOpen,
      'aria-controls': contentId,
    },
    content: {
      id: contentId,
      role: 'region',
      'aria-hidden': !isOpen,
    },
  };
}

/**
 * Get ARIA props for a checkbox
 */
export function getCheckboxProps(
  isChecked: boolean,
  isIndeterminate: boolean = false
): Record<string, unknown> {
  return {
    role: 'checkbox',
    'aria-checked': isIndeterminate ? 'mixed' : isChecked,
    tabIndex: 0,
  };
}

/**
 * Get ARIA props for a listbox item
 */
export function getListboxItemProps(
  isSelected: boolean,
  position: number,
  total: number
): Record<string, unknown> {
  return {
    role: 'option',
    'aria-selected': isSelected,
    'aria-posinset': position + 1,
    'aria-setsize': total,
  };
}

/**
 * Handle keyboard navigation in a list
 */
export function handleListNavigation(
  e: React.KeyboardEvent,
  items: HTMLElement[],
  currentIndex: number,
  onSelect: (index: number) => void
): void {
  switch (e.key) {
    case 'ArrowDown': {
      e.preventDefault();
      const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
      items[nextIndex]?.focus();
      break;
    }
    case 'ArrowUp': {
      e.preventDefault();
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
      items[prevIndex]?.focus();
      break;
    }
    case 'Home':
      e.preventDefault();
      items[0]?.focus();
      break;
    case 'End':
      e.preventDefault();
      items[items.length - 1]?.focus();
      break;
    case 'Enter':
    case ' ':
      e.preventDefault();
      onSelect(currentIndex);
      break;
  }
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Check if user is using keyboard navigation
 */
let isUsingKeyboard = false;

if (typeof document !== 'undefined') {
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Tab') {
      isUsingKeyboard = true;
    }
  });

  document.addEventListener('mousedown', () => {
    isUsingKeyboard = false;
  });
}

export function isKeyboardUser(): boolean {
  return isUsingKeyboard;
}

/**
 * Keyboard shortcuts helper
 */
export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  description: string;
  action: () => void;
}

export function matchesShortcut(e: KeyboardEvent, shortcut: Omit<KeyboardShortcut, 'description' | 'action'>): boolean {
  const ctrlMatch = (shortcut.ctrl ?? false) === (e.ctrlKey || e.metaKey);
  const shiftMatch = (shortcut.shift ?? false) === e.shiftKey;
  const altMatch = (shortcut.alt ?? false) === e.altKey;
  const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase();

  return ctrlMatch && shiftMatch && altMatch && keyMatch;
}

/**
 * Format shortcut for display (e.g., "⌘K" on Mac, "Ctrl+K" on Windows)
 */
export function formatShortcut(shortcut: Omit<KeyboardShortcut, 'description' | 'action'>): string {
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toLowerCase().includes('mac');

  const parts: string[] = [];

  if (shortcut.ctrl) {
    parts.push(isMac ? '⌘' : 'Ctrl');
  }
  if (shortcut.shift) {
    parts.push(isMac ? '⇧' : 'Shift');
  }
  if (shortcut.alt) {
    parts.push(isMac ? '⌥' : 'Alt');
  }

  // Format the key
  const key = shortcut.key.length === 1
    ? shortcut.key.toUpperCase()
    : shortcut.key;

  parts.push(key);

  return isMac ? parts.join('') : parts.join('+');
}

/**
 * Skip link target IDs
 */
export const SKIP_LINK_TARGETS = {
  MAIN_CONTENT: 'main-content',
  SIDEBAR: 'sidebar-navigation',
  TASK_LIST: 'task-list',
} as const;

/**
 * Create skip link props
 */
export function getSkipLinkProps(targetId: string): Record<string, unknown> {
  return {
    href: `#${targetId}`,
    className: 'sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-50 focus:p-4 focus:bg-surface-primary focus:text-text-primary',
  };
}
